// Deferred Sentry loader (perf). @sentry/react is NOT in the main bundle:
// `startSentry()` schedules a dynamic import for after window load + idle.
//
// Errors thrown before the SDK is up are not lost:
//  - `installEarlyErrorQueue()` (called synchronously from index.tsx, before
//    render) records window `error` / `unhandledrejection` events;
//  - `captureException()` (used by ErrorBoundary) queues too.
// Both are flushed into Sentry once it is initialised. The init options are
// identical to the previous eager init in index.tsx.

import { whenIdle } from "./idle";

type SentryModule = typeof import("@sentry/react");
type Ctx = { tags?: Record<string, string>; contexts?: Record<string, Record<string, unknown>> };

const MAX_QUEUE = 30;
let sentry: SentryModule | null = null;
let started = false;
let queue: Array<{ error: unknown; ctx?: Ctx }> = [];
let detach: (() => void) | null = null;
// Signed-in user id, kept so events captured before the SDK loads are tagged
// too. The id only: no email, no name. Sentry showed 0 identified users on
// every error before this, so "one user or everyone" was unanswerable.
let userId: string | null = null;

/** Tag future Sentry events with the signed-in user's id (null on sign-out). */
export function setSentryUser(id: string | null): void {
  userId = id;
  sentry?.setUser(id ? { id } : null);
}

function send(s: SentryModule, error: unknown, ctx?: Ctx) {
  s.withScope((scope) => {
    if (ctx?.tags) Object.entries(ctx.tags).forEach(([k, v]) => scope.setTag(k, v));
    if (ctx?.contexts) Object.entries(ctx.contexts).forEach(([k, v]) => scope.setContext(k, v));
    s.captureException(error);
  });
}

/** Report an exception now if Sentry is loaded, else queue it. */
export function captureException(error: unknown, ctx?: Ctx): void {
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  if (sentry) send(sentry, error, ctx);
  else if (queue.length < MAX_QUEUE) queue.push({ error, ctx });
}

/** Tiny listeners that buffer global errors until Sentry has loaded. */
export function installEarlyErrorQueue(): void {
  if (!process.env.REACT_APP_SENTRY_DSN || detach) return;
  const onError = (e: ErrorEvent) => captureException(e.error || e.message);
  const onRejection = (e: PromiseRejectionEvent) => captureException(e.reason);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  detach = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    detach = null;
  };
}

async function load(dsn: string): Promise<void> {
  try {
    const S = await import("@sentry/react");
    // Release tag — correlates errors to a specific deploy in the Sentry
    // dashboard. Reads from REACT_APP_VERSION (set in Vercel project env
    // to $VERCEL_GIT_COMMIT_SHA at build time). Falls back to "dev" for
    // local builds and "unknown" if the env var is missing in prod.
    // Format mirrors the backend: parcely-frontend@<version>.
    const version =
      process.env.REACT_APP_VERSION || (process.env.NODE_ENV === "development" ? "dev" : "unknown");
    // Sentry's own global handlers take over from here; drop our buffers
    // first so an error isn't reported twice.
    detach?.();
    S.init({
      dsn,
      // Route error reports through our own domain so ad blockers don't
      // intercept them (issue #352). See api/sentry-proxy.
      tunnel: "/api/sentry-proxy",
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
      release: `parcely-frontend@${version}`,
      tracesSampleRate: 0.1, // 10% of user sessions get a performance trace
      ignoreErrors: [
        // ResizeObserver firing during heavy layout — harmless
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // User-cancelled network requests
        "AbortError",
      ],
    });
    sentry = S;
    if (userId) S.setUser({ id: userId });
    const pending = queue;
    queue = [];
    pending.forEach(({ error, ctx }) => send(S, error, ctx));
    // Visible in DevTools console — operators can confirm which build a user
    // is running when triaging support tickets.
    // eslint-disable-next-line no-console
    console.info(`Parcely frontend ${version} (${process.env.REACT_APP_SENTRY_ENVIRONMENT || "production"})`);
  } catch {
    // Chunk blocked (ad blocker / offline): give up quietly.
    queue = [];
  }
}

/** Load + init Sentry after first paint. No-op without REACT_APP_SENTRY_DSN. */
export function startSentry(): void {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn || started) return;
  started = true;
  whenIdle(() => {
    void load(dsn);
  });
}
